import { EntryType } from '../../../../data/model/questionnaire'
import { SidebarEntry } from './SidebarQuestionnaireItemList.query'

export const getEntryListItemPropValues = (
  entry: SidebarEntry
): [string, string] => {
  const { contextPosition } = entry

  switch (entry.entryType) {
    case EntryType.TextCardEntryType: {
      const questionTitle = entry.entryItem.textCard.title || ''
      const displayNumber = `T${contextPosition + 1}`
      return [questionTitle, displayNumber]
    }
    case EntryType.MatrixEntryType: {
      const questionTitle = entry.entryItem.matrixTitle?.title || ''
      const displayNumber = `Q${contextPosition + 1}`
      return [questionTitle, displayNumber]
    }
    case EntryType.ForkEntryType: {
      const title = entry.entryItem.fork.name
      return [title, 'A/B']
    }
    case EntryType.SectionEntryType: {
      const title = `Section ${contextPosition + 1}`
      const displayNumber = `S${contextPosition + 1}`
      return [title, displayNumber]
    }
    case EntryType.TextHighlighterEntryType: {
      const questionText = entry.entryItem.question?.text || ''
      const displayNumber = `Q${contextPosition + 1}`
      return [questionText, displayNumber]
    }
    case EntryType.ConjointEntryType: {
      const questionText = entry.entryItem.question?.text || ''
      const displayNumber = `Q${contextPosition + 1}`
      return [questionText, displayNumber]
    }
    case EntryType.QuestionEntryType:
    default: {
      const questionTitle = entry.entryItem.question?.text || ''
      const displayNumber = `Q${contextPosition + 1}`
      return [questionTitle, displayNumber]
    }
  }
}

export const getFlatSidebarEntries = (entries: SidebarEntry[]) => {
  return entries.flatMap((entry) => {
    if (entry.entryType === EntryType.SectionEntryType) {
      const sectionItem = entry.entryItem
      const newSectionItem = { ...sectionItem, entries: [] }
      const sectionEntry = {
        ...entry,
        entryItem: newSectionItem
      }
      return [sectionEntry, ...sectionItem.entries]
    }
    return entry
  })
}

export const getNestedSidebarEntries = (entries: SidebarEntry[]) => {
  return entries.reduce((previousValue, currentEntry) => {
    if (currentEntry.sectionId) {
      const section = previousValue.find(
        (a) =>
          a.entryType === EntryType.SectionEntryType &&
          a.entryItem.sectionId === currentEntry.sectionId
      )
      if (section && section.entryType === EntryType.SectionEntryType) {
        const { entries } = section.entryItem
        entries.push(currentEntry)
        const newSectionItem = {
          ...section.entryItem,
          entries
        }
        const newSection = {
          ...section,
          entryItem: newSectionItem
        }
        const sectionIndex = previousValue.indexOf(section)
        previousValue.splice(sectionIndex, 1, newSection)
      }
      return previousValue
    }

    previousValue.push(currentEntry)
    return previousValue
  }, [] as SidebarEntry[])
}

export const reorderEntries = (
  flatEntries: SidebarEntry[],
  from: number,
  to: number,
  sectionId?: string
) => {
  if (
    flatEntries.length === 0 ||
    from >= flatEntries.length ||
    to >= flatEntries.length
  ) {
    return flatEntries
  }

  let itemToMoveCount = 1
  const item = flatEntries[from]
  if (item.entryType === EntryType.SectionEntryType) {
    // if a section is moved all of its children should be also moved to new positions
    const childrenCount = flatEntries.filter(
      (e) => e.sectionId === item.entryItem.sectionId
    ).length
    itemToMoveCount += childrenCount
  }
  const [itemToMove, ...childrenItems] = flatEntries.splice(
    from,
    itemToMoveCount
  )
  const updated = {
    ...itemToMove,
    sectionId: sectionId || itemToMove.sectionId
  }
  // if a section is moved drop position should be adjusted with the number of children
  const dropPosition =
    from < to && item.entryType === EntryType.SectionEntryType
      ? to - itemToMoveCount + 1
      : to
  flatEntries.splice(dropPosition, 0, updated, ...childrenItems)
  return flatEntries
}
